import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Slider,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  TextField,
  Select,
  Drawer,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../../../request/http-common';
import { setTradings } from '../../../request/Analysis/hitorySlice';

// Styled components
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '32px',
    fontSize: '12px',
    backgroundColor: '#1E2329',
    border: '1px solid #2B3139',
    borderRadius: '4px',
    color: 'white',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #F7A600',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 12px',
    fontSize: '12px',
    '&::placeholder': {
      color: '#848E9C',
      opacity: 1,
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  height: '32px',
  fontSize: '12px',
  backgroundColor: '#1E2329',
  border: '1px solid #2B3139',
  borderRadius: '4px',
  color: 'white',
  '& .MuiSelect-select': {
    padding: '6px 12px',
    paddingRight: '32px !important',
    fontSize: '12px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #F7A600',
  },
  '& .MuiSvgIcon-root': {
    color: '#848E9C',
    right: '8px',
  },
}));

const Limit = ({ orderType, balance, coinPrice, currentPair, tradingType }) => {
  const dispatch = useDispatch();
  const pair = useSelector((state) => state.currentPair.currentPair);
  
  // Form state
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const [quantitySlider, setQuantitySlider] = useState(0);
  const [timeInForce, setTimeInForce] = useState('GTC');
  const [postOnly, setPostOnly] = useState(false);
  const [reduceOnly, setReduceOnly] = useState(false);
  const [leverage, setLeverage] = useState('10');
  const [marginType, setMarginType] = useState('Cross');
  const [duration, setDuration] = useState('');
  const [error, setError] = useState('');
  const [orderValue, setOrderValue] = useState('0.00');
  
  // Confirmation drawer
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingOrderSide, setPendingOrderSide] = useState('');

  // Trading plans
  const [tradingPlans, setTradingPlans] = useState([]);
  
  // Fetch trading plans
  const { isLoading: isLoadingPlans } = useQuery(
    "trading-plans",
    async () => {
      return await apiClient.get("/api/trading-plans");
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setTradingPlans(res.data.trading_plans);
        }
      },
      onError: (err) => {
        console.log('Error loading plans:', err.response?.data || err);
      },
    }
  );

  // Utility functions
  const formatPrice = (value) => parseFloat(value || 0).toFixed(2);
  const formatQuantity = (value) => parseFloat(value || 0).toFixed(4);
  const formatBalance = (value) => parseFloat(value || 0).toFixed(2);

  const calculateOrderValue = () => {
    const priceValue = parseFloat(price || 0);
    const quantityValue = parseFloat(quantity || 0);
    
    if (priceValue > 0 && quantityValue > 0) {
      const value = priceValue * quantityValue;
      return formatPrice(value);
    }
    return '0.00';
  };

  const calculateMaxQuantity = () => {
    if (balance && price && parseFloat(price) > 0) {
      return formatQuantity(parseFloat(balance) / parseFloat(price));
    }
    return '0.0000';
  };

  // Update order value whenever price, quantity, or coinPrice changes
  useEffect(() => {
    const newOrderValue = calculateOrderValue();
    setOrderValue(newOrderValue);
  }, [price, quantity, coinPrice]);

  const handlePriceChange = (e) => {
    setPrice(e.target.value);
  };

  const handleQuantityChange = (e) => {
    setQuantity(e.target.value);
  };

  const handleQuantitySliderChange = (event, newValue) => {
    setQuantitySlider(newValue);
    
    // If no price is set, automatically use market price for limit orders
    let priceToUse = price && parseFloat(price) > 0 ? parseFloat(price) : parseFloat(coinPrice);
    
    // Auto-set price to market price if it's empty when using slider
    if (!price || parseFloat(price) <= 0) {
      setPrice(formatPrice(coinPrice));
      priceToUse = parseFloat(coinPrice);
    }
    
    if (balance && priceToUse > 0) {
      const maxQty = parseFloat(balance) / priceToUse;
      const newQuantity = (maxQty * newValue) / 100;
      setQuantity(formatQuantity(newQuantity));
    }
  };

  const handlePercentageClick = (percentage) => {
    // If no price is set, automatically use market price for limit orders
    let priceToUse = price && parseFloat(price) > 0 ? parseFloat(price) : parseFloat(coinPrice);
    
    // Auto-set price to market price if it's empty when using percentage buttons
    if (!price || parseFloat(price) <= 0) {
      setPrice(formatPrice(coinPrice));
      priceToUse = parseFloat(coinPrice);
    }
    
    if (balance && priceToUse > 0) {
      const maxQty = parseFloat(balance) / priceToUse;
      const newQuantity = (maxQty * percentage) / 100;
      setQuantity(formatQuantity(newQuantity));
      setQuantitySlider(percentage);
    }
  };

  const setCurrentPrice = () => {
    setPrice(formatPrice(coinPrice));
  };

  // Submit mutation
  const { isLoading: isSubmitting, mutate: submitOrder } = useMutation(
    async (orderData) => {
      console.log('Submitting order:', orderData);
      return await apiClient.post(`/api/start-trading`, orderData);
    },
    {
      onSuccess: (res) => {
        console.log('Order response:', res.data);
        if (res.data.status === 'success') {
          dispatch(setTradings(res.data.tradings));
          localStorage.setItem('notification', JSON.stringify({
            message: `${pendingOrderSide === 'buy' ? 'Buy' : 'Sell'} limit order placed successfully!`,
            'type': 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          
          // Reset form
          setPrice('');
          setQuantity('');
          setQuantitySlider(0);
          setPostOnly(false);
          setReduceOnly(false);
          setError('');
          setConfirmationOpen(false);
          setPendingOrderSide('');
        }
      },
      onError: (err) => {
        console.error('Order error:', err);
        if (err.response?.data?.errors) {
          setError(Object.values(err.response.data.errors).flat().join(', '));
        } else {
          let errorMessage = err.response?.data?.message || 'Failed to place order';
          setError(errorMessage);
          localStorage.setItem('notification', JSON.stringify({
            message: errorMessage,
            'type': 'error'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
        setConfirmationOpen(false);
      },
    }
  );

  const validateOrder = () => {
    if (!price || parseFloat(price) <= 0) {
      setError('Please enter a valid price');
      return false;
    }
    
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Please enter a valid quantity');
      return false;
    }
    
    if (!duration) {
      setError('Please select a duration');
      return false;
    }
    
    const orderValueNum = parseFloat(orderValue);
    const balanceNum = parseFloat(balance);
    
    if (orderValueNum > balanceNum) {
      setError(`Insufficient balance. Required: ${orderValue} USDT, Available: ${formatBalance(balance)} USDT`);
      return false;
    }
    
    return true;
  };

  const handleOrderClick = (side) => {
    console.log('Order click:', side);
    setError('');
    
    if (!validateOrder()) {
      return;
    }
    
    setPendingOrderSide(side);
    setConfirmationOpen(true);
  };

  const confirmOrder = () => {
    console.log('Confirming order...');
    
    const orderData = {
      // Basic order fields
      amount: parseFloat(orderValue),
      stop_loss: 0,
      take_profit: 0,
      timer_units: parseInt(duration),
      trading_pair: pair || currentPair,
      order_type: 'Limit',
      pair: pair || currentPair,
      entry_point: parseFloat(coinPrice).toFixed(6),
      side: pendingOrderSide,
      
      // Advanced order fields
      trading_type: tradingType || 'Spot',
      limit_price: parseFloat(price),
      order_quantity: parseFloat(quantity),
      post_only: postOnly,
      reduce_only: reduceOnly,
      time_in_force: timeInForce,
      
      // Futures specific
      leverage: tradingType === 'Futures' ? parseFloat(leverage) : null,
      margin_type: tradingType === 'Futures' ? marginType : null,
    };
    
    console.log('Final order data:', orderData);
    submitOrder(orderData);
  };

  const getSelectedPlan = () => {
    return tradingPlans.find(plan => plan.id.toString() === duration.toString());
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Available Balance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
          Available
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', fontSize: '11px', fontWeight: 500 }}>
          {formatBalance(balance)} USDT
        </Typography>
      </Box>

      {/* Cross/Isolated and Leverage - Only for Futures */}
      {tradingType === 'Futures' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <StyledSelect 
            size="small" 
            value={marginType} 
            onChange={(e) => setMarginType(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </StyledSelect>
          <StyledSelect 
            size="small" 
            value={leverage}
            onChange={(e) => setLeverage(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value="10">10x</MenuItem>
            <MenuItem value="20">20x</MenuItem>
            <MenuItem value="50">50x</MenuItem>
            <MenuItem value="100">100x</MenuItem>
          </StyledSelect>
        </Box>
      )}

      {/* Price Input */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px' }}>
            Price (USDT)
          </Typography>
          <Button
            size="small"
            onClick={setCurrentPrice}
            sx={{
              color: '#F7A600',
              fontSize: '10px',
              minWidth: 'auto',
              p: 0,
              textTransform: 'none'
            }}
          >
            Market
          </Button>
        </Box>
        <StyledTextField
          fullWidth
          size="small"
          value={price}
          onChange={handlePriceChange}
          placeholder={formatPrice(coinPrice)}
        />
      </Box>

      {/* Quantity Input */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Quantity ({currentPair})
        </Typography>
        <StyledTextField
          fullWidth
          size="small"
          value={quantity}
          onChange={handleQuantityChange}
          placeholder="0.0000"
        />
        {tradingType === 'Spot' && price && (
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px', mt: 0.5, display: 'block' }}>
            Max buy: {calculateMaxQuantity()} {currentPair}
          </Typography>
        )}
      </Box>

      {/* Quantity Slider */}
      <Box sx={{ mb: 1.5, px: 0.5 }}>
        <Slider
          value={quantitySlider}
          onChange={handleQuantitySliderChange}
          min={0}
          max={100}
          size="small"
          sx={{
            color: '#F7A600',
            height: 4,
            '& .MuiSlider-track': { backgroundColor: '#F7A600' },
            '& .MuiSlider-thumb': { 
              backgroundColor: '#F7A600',
              width: 12,
              height: 12,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(247, 166, 0, 0.16)'
              }
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          {[0, 25, 50, 75, 100].map(value => (
            <Typography 
              key={value} 
              variant="caption" 
              sx={{ 
                color: '#848E9C', 
                fontSize: '9px',
                cursor: 'pointer',
                '&:hover': { color: '#F7A600' }
              }}
              onClick={() => handlePercentageClick(value)}
            >
              {value}%
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Duration */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Duration
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">Select Duration</MenuItem>
            {tradingPlans.length > 0 && tradingPlans.map(plan => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.duration}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Time in Force */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '11px', mb: 0.5, display: 'block' }}>
          Time in Force
        </Typography>
        <FormControl fullWidth size="small">
          <StyledSelect
            value={timeInForce}
            onChange={(e) => setTimeInForce(e.target.value)}
          >
            <MenuItem value="GTC">GTC (Good Till Canceled)</MenuItem>
            <MenuItem value="IOC">IOC (Immediate or Cancel)</MenuItem>
            <MenuItem value="FOK">FOK (Fill or Kill)</MenuItem>
          </StyledSelect>
        </FormControl>
      </Box>

      {/* Order Summary */}
      <Box sx={{ 
        bgcolor: '#181A20', 
        borderRadius: 1, 
        p: 1, 
        mb: 1.5,
        border: '1px solid #2B3139'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Order Value
          </Typography>
          <Typography variant="caption" sx={{ color: '#0ECB81', fontSize: '10px', fontWeight: 500 }}>
            {orderValue} USDT
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            Est. Fee
          </Typography>
          <Typography variant="caption" sx={{ color: '#848E9C', fontSize: '10px' }}>
            {formatPrice(parseFloat(orderValue) * 0.001)} USDT
          </Typography>
        </Box>
      </Box>

      {/* Options */}
      <Box sx={{ mb: 1.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={postOnly}
              onChange={(e) => setPostOnly(e.target.checked)}
              size="small"
              sx={{ 
                color: '#848E9C',
                '&.Mui-checked': { color: '#F7A600' },
                padding: '4px'
              }}
            />
          }
          label={
            <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
              Post-Only
            </Typography>
          }
          sx={{ ml: 0, mb: 0.5 }}
        />
        {tradingType === 'Futures' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={reduceOnly}
                onChange={(e) => setReduceOnly(e.target.checked)}
                size="small"
                sx={{ 
                  color: '#848E9C',
                  '&.Mui-checked': { color: '#F7A600' },
                  padding: '4px'
                }}
              />
            }
            label={
              <Typography sx={{ color: '#848E9C', fontSize: '11px' }}>
                Reduce-Only
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('buy')}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#0ECB81',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: '#0BB76D'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : (tradingType === 'Futures' ? 'Long' : 'Buy')} {currentPair}
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={() => handleOrderClick('sell')}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#F6465D',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            py: 1,
            height: '36px',
            '&:hover': { 
              bgcolor: '#E13B52'
            },
            '&:disabled': {
              bgcolor: '#2B3139',
              color: '#848E9C'
            }
          }}
        >
          {isSubmitting ? 'Processing...' : (tradingType === 'Futures' ? 'Short' : 'Sell')} {currentPair}
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mt: 1.5, p: 1, bgcolor: '#2B1617', borderRadius: 1, border: '1px solid #F6465D' }}>
          <Typography variant="caption" sx={{ color: '#F6465D', fontSize: '10px' }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* Confirmation Drawer */}
      <Drawer
        anchor="bottom"
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: '#1E2329',
            color: 'white',
            maxHeight: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 99999,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
          }
        }}
        ModalProps={{
          sx: {
            zIndex: 99999,
          }
        }}
        sx={{
          zIndex: 99999,
          '& .MuiDrawer-paper': {
            zIndex: 99999,
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
            Confirm {pendingOrderSide === 'buy' ? 'Buy' : 'Sell'} Order
          </Typography>
          
          <Divider sx={{ mb: 2, bgcolor: '#2B3139' }} />
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Order Type:</Typography>
              <Typography variant="body2">Limit</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Trading Type:</Typography>
              <Typography variant="body2">{tradingType || 'Spot'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Pair:</Typography>
              <Typography variant="body2">{pair || currentPair}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Side:</Typography>
              <Typography variant="body2" color={pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D'}>
                {pendingOrderSide === 'buy' ? (tradingType === 'Futures' ? 'Long' : 'Buy') : (tradingType === 'Futures' ? 'Short' : 'Sell')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Price:</Typography>
              <Typography variant="body2">{price} USDT</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Quantity:</Typography>
              <Typography variant="body2">{quantity} {currentPair}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Order Value:</Typography>
              <Typography variant="body2" fontWeight="bold">{orderValue} USDT</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Duration:</Typography>
              <Typography variant="body2">{getSelectedPlan()?.duration || 'N/A'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Time in Force:</Typography>
              <Typography variant="body2">{timeInForce}</Typography>
            </Box>
            {tradingType === 'Futures' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#848E9C">Leverage:</Typography>
                  <Typography variant="body2">{leverage}x</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#848E9C">Margin:</Typography>
                  <Typography variant="body2">{marginType}</Typography>
                </Box>
              </>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#848E9C">Est. Fee:</Typography>
              <Typography variant="body2">{formatPrice(parseFloat(orderValue) * 0.001)} USDT</Typography>
            </Box>
          </Box>

          {(postOnly || reduceOnly) && (
            <Alert severity="info" sx={{ mb: 2, bgcolor: '#181A20', color: 'white' }}>
              {postOnly && 'Post-Only enabled. '}
              {reduceOnly && 'Reduce-Only enabled.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setConfirmationOpen(false)}
              disabled={isSubmitting}
              sx={{
                borderColor: '#2B3139',
                color: '#848E9C',
                '&:hover': {
                  borderColor: '#848E9C',
                  bgcolor: 'transparent'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmOrder}
              disabled={isSubmitting}
              sx={{
                bgcolor: pendingOrderSide === 'buy' ? '#0ECB81' : '#F6465D',
                '&:hover': {
                  bgcolor: pendingOrderSide === 'buy' ? '#0BB76D' : '#E13B52'
                }
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Placing Order...
                </>
              ) : (
                `Confirm ${pendingOrderSide === 'buy' ? 'Buy' : 'Sell'}`
              )}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Limit;
